import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FocusTrap } from "@/components/a11y/FocusTrap";

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface ShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  label: string;
  keys: string[];
}

interface ShortcutGroup {
  category: string;
  shortcuts: Shortcut[];
}

function getShortcuts(t: (key: string) => string): ShortcutGroup[] {
  return [
    {
      category: t("shortcuts.chat"),
      shortcuts: [
        { label: t("shortcuts.newConversation"), keys: ["mod", "Shift", "N"] },
        { label: t("shortcuts.stopStreaming"), keys: ["Esc"] },
        { label: t("shortcuts.sendMessage"), keys: ["Enter"] },
        { label: t("shortcuts.newLine"), keys: ["Shift", "Enter"] },
      ],
    },
    {
      category: t("shortcuts.navigation"),
      shortcuts: [
        { label: t("shortcuts.commandPalette"), keys: ["mod", "K"] },
        { label: t("shortcuts.toggleSidebar"), keys: ["mod", "B"] },
        { label: t("shortcuts.openSettings"), keys: ["mod", ","] },
        { label: t("shortcuts.showShortcuts"), keys: ["?"] },
      ],
    },
  ];
}

function KeyBadge({ value }: { value: string }) {
  const display = value === "mod" ? (isMac ? "⌘" : "Ctrl") : value;
  return (
    <kbd className="cx-kbd">{display}</kbd>
  );
}

export function ShortcutsDialog({ open, onClose }: ShortcutsDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Trap focus and close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose]);

  // Focus the dialog when opened
  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const groups = getShortcuts(t);

  return (
    <FocusTrap>
    <div className="cx-shortcuts-backdrop animate-cx-fade-in" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-label={t("shortcuts.title")}
        tabIndex={-1}
        className="cx-shortcuts-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cx-dialog-header flex items-center justify-between px-5 py-4">
          <h2 className="cx-dialog-title text-[15px] font-medium">
            {t("shortcuts.title")}
          </h2>
          <button
            onClick={onClose}
            className="cx-msg-action"
            aria-label={t("common.close")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.category}>
              <h3
                className="cx-text-placeholder text-[11px] font-semibold uppercase tracking-wider mb-2.5"
              >
                {group.category}
              </h3>
              <div className="flex flex-col gap-1">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.label}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="cx-text-secondary text-[13px]">
                      {shortcut.label}
                    </span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, i) => (
                        <KeyBadge key={`${key}-${i}`} value={key} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </FocusTrap>
  );
}
